import { useEffect, useMemo, useRef } from 'react'
import { useReducedMotion } from 'framer-motion'
import type { SourceLike, WorldBounds } from '../../../../lib/simulation-core'
import { SimulationCanvasStage, SimulationLayout, SimulationControlsBar } from '../../../../components/simulation'
import type { VehicleConfig, VehicleState } from '../engine/types'
import { useBraitenbergSim } from '../hooks/useBraitenbergSim'

interface VehicleDemoProps {
  configs: VehicleConfig[]
  initialStates: VehicleState[]
  sources: SourceLike[]
  bounds: WorldBounds
  reducedMotionOverride?: boolean
}

function formatValue(value: number | undefined): string {
  return Number.isFinite(value) ? Math.max(0, value ?? 0).toFixed(2) : '0.00'
}

export function VehicleDemo({ configs, initialStates, sources, bounds, reducedMotionOverride }: VehicleDemoProps) {
  const prefersReducedMotion = useReducedMotion() ?? false
  const reducedMotion = reducedMotionOverride ?? prefersReducedMotion
  const containerRef = useRef<HTMLDivElement>(null)
  const resumeWhenVisibleRef = useRef(false)
  const stableConfigs = useMemo(() => configs, [])
  const stableStates = useMemo(() => initialStates, [])
  const stableSources = useMemo(() => sources, [])
  const { canvasRef, simulation, onCanvasResize, reset, telemetry } = useBraitenbergSim({
    configs: stableConfigs,
    initialStates: stableStates,
    initialSources: stableSources,
    bounds,
    autoStart: !reducedMotion,
  })

  useEffect(() => {
    if (reducedMotion) simulation.stop()
  }, [reducedMotion, simulation.stop])

  useEffect(() => {
    const element = containerRef.current
    if (!element || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting && simulation.running) {
        resumeWhenVisibleRef.current = true
        simulation.stop()
      } else if (entry.isIntersecting && resumeWhenVisibleRef.current && !reducedMotion) {
        resumeWhenVisibleRef.current = false
        simulation.start()
      }
    }, { rootMargin: '160px 0px' })
    observer.observe(element)
    return () => observer.disconnect()
  }, [reducedMotion, simulation.running, simulation.start, simulation.stop])

  return (
    <div ref={containerRef} className="vehicle-demo">
      <SimulationLayout
        canvas={<SimulationCanvasStage canvasRef={canvasRef} onResize={onCanvasResize} ariaLabel="Braitenberg vehicle simulation; drag the glowing source" />}
        controls={(
          <>
            <div className="vehicle-telemetry" aria-live="off">
              {telemetry.map((vehicle) => (
                <dl key={vehicle.label}>
                  <div><dt>Vehicle</dt><dd>{vehicle.label}</dd></div>
                  <div><dt>Sensors</dt><dd>{vehicle.sensors.length ? vehicle.sensors.map(formatValue).join(' · ') : '—'}</dd></div>
                  <div><dt>Motors L · R</dt><dd>{formatValue(vehicle.leftMotor)} · {formatValue(vehicle.rightMotor)}</dd></div>
                </dl>
              ))}
            </div>
            {reducedMotion ? <p className="reduced-motion-note">Reduced motion is enabled. Use Step to advance the mechanism.</p> : null}
            <SimulationControlsBar running={simulation.running} onToggle={simulation.toggle} onReset={reset} onStep={simulation.stepOnce} reducedMotion={reducedMotion} />
          </>
        )}
      />
    </div>
  )
}
